import { IconCheck16, IconInfo16 } from '@warp-ds/icons/react';
import { Alert } from '@warp-ds/react';
import type React from 'react';
import { useState } from 'react';
import { FinnBox } from '@/components/finn/FinnBox';
import { FinnBoxMatrix } from '@/components/finn/FinnBoxMatrix';
import { FinnBoxRepublish } from '@/components/finn/FinnBoxRepublish';
import { FinnBoxRepublishMatrix } from '@/components/finn/FinnBoxRepublishMatrix';
import { PageNavigator, PageNumber } from '@/components/pagenavigator/PageNavigator';
import { QasaBox } from '@/components/qasa/QasaBox';
import { QasaBoxMatrix } from '@/components/qasa/QasaBoxMatrix';
import { QasaBoxRepublish } from '@/components/qasa/QasaBoxRepublish';
import { QasaBoxRepublishMatrix } from '@/components/qasa/QasaBoxRepublishMatrix';
import { useExperiment } from '@/hooks/experiments';

type Props = {
    itemId?: string;
    error?: string;
};

interface ScrollEvent extends React.UIEvent<HTMLDivElement> {
    target: HTMLDivElement & EventTarget;
}

type ComparisonRowProps = {
    label: string;
    info?: boolean;
    finn?: boolean;
    qasa?: boolean;
};

const ComparisonRow = ({ label, info, qasa, finn }: ComparisonRowProps) => (
    <tr style={{ height: 90 }}>
        <td className="w-1/3 pl-12 pr-16 md:pr-0 border-b s-border align-middle">
            <span className="flex items-center gap-4">
                {label}
                {info && <IconInfo16 className="s-text-subtle flex-none ml-4" aria-hidden />}
            </span>
        </td>
        <td className="w-1/3 border-b border-l border-r s-border align-middle">
            <div className="flex justify-center">{qasa && <IconCheck16 className="s-text-positive w-24 h-24" aria-label="Ja" />}</div>
        </td>
        <td className="w-1/3 border-b s-border align-middle">
            <div className="flex justify-center">{finn && <IconCheck16 className="s-text-positive w-24 h-24" aria-label="Ja" />}</div>
        </td>
    </tr>
);

const experimentConfig = {
    name: 'travel-qasa-platform-selector-matrix',
    id: 8,
};

const PlatformSelectionPage = ({ itemId, error }: Props) => {
    const experiment = useExperiment();
    const variant = experiment?.[experimentConfig.name] ?? 'control';
    const [activeBox, setActiveBox] = useState(PageNumber.ONE);
    const snapCenterStyle = {
        width: '85%',
        scrollSnapAlign: 'center',
    };

    const isTreatment = variant === 'treatment';

    const QasaComponent = itemId ? (isTreatment ? QasaBoxRepublishMatrix : QasaBoxRepublish) : isTreatment ? QasaBoxMatrix : QasaBox;

    const FinnComponent = itemId ? (isTreatment ? FinnBoxRepublishMatrix : FinnBoxRepublish) : isTreatment ? FinnBoxMatrix : FinnBox;

    const handleScroll = (e: ScrollEvent): void => {
        const scrollLeft = e.target.scrollLeft;
        const totalWidth = e.target.scrollWidth - e.target.clientWidth;

        if (scrollLeft < totalWidth / 2) {
            setActiveBox(PageNumber.ONE);
        } else {
            setActiveBox(PageNumber.TWO);
        }
    };

    return (
        <main className="mb-64 page-container">
            <h1 className="my-16 md:my-32">{itemId ? 'Republiser din bolig' : 'Hvordan vil du leie ut din bolig?'}</h1>
            {error === 'true' && (
                <div className="mb-16">
                    <Alert type="negative" show>
                        Ushda her gikk noe galt. Vi klarte ikke republisere annonsen på Qasa. Vennligst prøv igjen. Om du har prøvd å
                        republisere denne annonsen tidligere kan du se om den ligger under mine annonser på qasa.com.
                    </Alert>
                </div>
            )}
            <div
                className="flex flex-row md:space-y-0 space-x-10 md:space-x-16 -mr-16 md:mr-0"
                onScroll={handleScroll}
                style={{ scrollSnapType: 'x mandatory', overflowX: 'auto' }}>
                <div className="shrink-0 md:shrink md:min-w-0 md:max-w-1/2" style={snapCenterStyle}>
                    <QasaComponent itemId={itemId} experiment={experimentConfig.id} variant={variant} />
                </div>
                <div className="shrink-0 md:shrink md:min-w-0 md:max-w-1/2 mr-16!" style={snapCenterStyle}>
                    <FinnComponent itemId={itemId} variant={variant} />
                </div>
            </div>
            <div id="page-navigator" className="md:hidden mt-24 flex justify-center">
                <PageNavigator activeBox={activeBox} />
            </div>
            {isTreatment && (
                <div className="mt-32">
                    <h2 className="mb-16">Sammenlign Qasa og FINN</h2>
                    <table className="table! w-full border-separate border-spacing-0 s-text text-14 border s-border rounded-8">
                        <thead>
                            <tr style={{ height: 90 }}>
                                <th className="w-1/3 pl-12 pr-16 md:pr-0 text-left border-b s-border align-middle" />
                                <th className="w-1/3 text-center border-b border-l border-r s-border align-middle">Qasa</th>
                                <th className="w-1/3 text-center border-b s-border align-middle">FINN</th>
                            </tr>
                        </thead>
                        <tbody>
                            <ComparisonRow label="Annonse på FINN" qasa finn />
                            <ComparisonRow label="Varslinger på mail til de som har lagret søk" qasa finn />
                            <ComparisonRow label="Digitalt kontrakt" qasa />
                            <ComparisonRow label="Annonse på Qasa" qasa />
                            <ComparisonRow label="Garantert innbetaling av husleie" qasa />
                            <ComparisonRow label="Utvidet utleieforsikring" qasa />
                            <ComparisonRow label="Garantert depositum" qasa />
                            <ComparisonRow label="Automatisk fakturering av husleie" qasa />
                            <ComparisonRow label="Dedikert eksperthjelp" qasa />
                        </tbody>
                    </table>
                </div>
            )}
        </main>
    );
};

export default PlatformSelectionPage;
